/**
 * Détail d'une rubrique issue de BlueNote Pocket, ligne par ligne :
 * donnée encodée dans Pocket → traduction (personne ou intervenant du document, valeur) → champ du formulaire.
 * Les sources de chaque champ (prefillSummary[].sources) sont fournies par le serveur depuis l'import ; pour une
 * rubrique importée avant, elles sont retrouvées en comparant les valeurs reprises aux données encodées.
 */

export const TRACE_STATUS = {
  mapped: 'mapped',
  used: 'used',
  unmapped: 'unmapped',
  rejected: 'rejected',
  empty: 'empty'
};

const UNMAPPED_NOTE = {
  mapped: 'Aucun champ correspondant : à reprendre vous-même si nécessaire.',
  linked: 'Pas de reprise automatique pour cette rubrique : à reprendre vous-même.',
  unresolved: 'Choisissez d’abord la rubrique BlueNote correspondante.'
};

const matchesSource = (sources, key) => Array.isArray(sources) && sources.some((s) => s === key || s.startsWith(`${key}:`));

const refText = (ref) => (ref.comment ? `${ref.label} (${ref.comment})` : ref.label);

/**
 * Personnes et intervenants d'un élément : un groupe par rôle (« Personne interpellée », « Collègue qui fouille »…)
 * pour le catalogue 2.0, les deux listes génériques pour les exports 1.0. Clé = source enregistrée à l'import.
 */
export const referenceGroups = (display) => {
  const roles = Array.isArray(display?.roles) ? display.roles : [];
  const covered = new Set(roles.flatMap((r) => (r.refs || []).map((ref) => ref.refId)));
  const groups = roles.map((r) => ({ key: `role:${r.key}`, label: r.label, refs: r.refs || [] }));
  const persons = (display?.persons || []).filter((p) => !covered.has(p.refId));
  const intervenants = (display?.intervenants || []).filter((i) => !covered.has(i.refId));
  if (persons.length > 0) groups.push({ key: 'persons', label: 'Personne(s) concernée(s)', refs: persons });
  if (intervenants.length > 0) groups.push({ key: 'intervenants', label: 'Intervenants', refs: intervenants });
  return groups;
};

const ROW_LABEL = { persons: 'Personne concernée', intervenants: 'Intervenant' };

const normalize = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

/** Sources probables d'un champ prérempli sans sources enregistrées (import antérieur). */
const guessSources = (entry, display) => {
  if (Array.isArray(entry.references)) {
    return referenceGroups(display)
      .filter((g) => g.refs.some((ref) => entry.references.includes(ref.refId)))
      .map((g) => g.key);
  }
  const value = normalize(entry.display);
  // « Oui » / « Non » ne permettent pas de retrouver la question d'origine
  if (!value || value === 'oui' || value === 'non') return [];
  const keys = [];
  (display.times || []).filter((t) => t.state === 'set').forEach((t) => {
    const date = (String(t.display).match(/\d{2}\/\d{2}\/\d{4}/) || [])[0];
    const hour = (String(t.display).match(/\d{2}:\d{2}/) || [])[0];
    if (date && hour && value.includes(date) && value.includes(hour)) keys.push(`time:${t.key}`);
    else if (date && value === date) keys.push(`time:${t.key}:date`);
    else if (hour && value === hour) keys.push(`time:${t.key}:hour`);
  });
  (display.answers || []).filter((a) => a.state === 'set').forEach((a) => {
    const answer = normalize(a.display);
    if (answer && (answer === value || value.split(', ').includes(answer))) keys.push(`answer:${a.key}`);
  });
  if (typeof display.notes === 'string') {
    const start = normalize(display.notes).slice(0, 40);
    if (start.length >= 3 && value.includes(start)) keys.push('notes');
  }
  return keys;
};

/**
 * @param {object} pocket  rubric.pocket
 * @param {Map<string, string>} entityLabels  identifiant d'entité du document → libellé courant
 * @returns {{ rows: Array<{ key, label, value, entity, targets: Array<{ field, label, value }>, status, note }>, traced: boolean }}
 */
export const buildPocketTrace = (pocket, entityLabels) => {
  const display = (pocket && pocket.display) || {};
  const recorded = Array.isArray(pocket?.prefillSummary) ? pocket.prefillSummary : [];
  const traced = recorded.every((s) => Array.isArray(s.sources));
  const summary = traced ? recorded : recorded.map((s) => ({ ...s, sources: guessSources(s, display) }));
  const rejected = Array.isArray(pocket?.rejected) ? pocket.rejected : [];
  const links = new Map((pocket?.entityLinks || []).map((l) => [l.refId, l.entityId]));
  const selfRefs = new Set(Array.isArray(pocket?.selfRefs) ? pocket.selfRefs : []);
  const unmappedNote = UNMAPPED_NOTE[pocket?.status] || UNMAPPED_NOTE.mapped;
  const unmappedKeys = new Set((pocket?.unmapped || []).map((u) => u.key));
  const usedFields = new Set();

  const entityName = (refId) => {
    const id = links.get(refId);
    if (!id) return null;
    return entityLabels.get(String(id)) || 'Entité supprimée de l’encodage';
  };

  const targetsFor = (key, refId) => summary
    .filter((s) => matchesSource(s.sources, key) && (!refId || !Array.isArray(s.references) || s.references.includes(refId)))
    .map((s) => {
      usedFields.add(s.field);
      const value = Array.isArray(s.references) ? (refId ? entityName(refId) : s.display) : s.display;
      return { field: s.field, label: s.label, value: value || s.display };
    });

  const partialTimeNote = (key) => {
    if (!key.startsWith('time:') || !unmappedKeys.has(key)) return '';
    const sources = summary.flatMap((s) => (matchesSource(s.sources, key) ? s.sources : []));
    if (sources.includes(`${key}:date`) && !sources.includes(`${key}:hour`)) return 'Seule la date est reprise : vérifiez l’heure.';
    if (sources.includes(`${key}:hour`) && !sources.includes(`${key}:date`)) return 'Seule l’heure est reprise : vérifiez la date.';
    return '';
  };

  const row = ({ key, label, value, entity = null, refId = null, isSet = true }) => {
    const base = { key: refId ? `${key}:${refId}` : key, label, value, entity };
    const targets = isSet ? targetsFor(key, refId) : [];
    if (targets.length > 0) return { ...base, targets, status: TRACE_STATUS.mapped, note: partialTimeNote(key) };
    const refusal = rejected.find((r) => matchesSource(r.sources, key));
    if (refusal) {
      return { ...base, targets, status: TRACE_STATUS.rejected, note: `Non repris dans « ${refusal.label} » : ${refusal.reason}.` };
    }
    if (!isSet) return { ...base, targets, status: TRACE_STATUS.empty, note: 'Rien à reprendre.' };
    if (refId && selfRefs.has(refId)) return { ...base, targets, status: TRACE_STATUS.used, note: 'C’est vous : aucun collègue à indiquer.' };
    if (unmappedKeys.has(key)) return { ...base, targets, status: TRACE_STATUS.unmapped, note: unmappedNote };
    return {
      ...base,
      targets,
      status: TRACE_STATUS.used,
      note: traced ? 'Pris en compte dans le choix de la rubrique.' : 'Pris en compte dans le formulaire.'
    };
  };

  const rows = [];
  (display.times || [])
    .filter((t) => t.state !== 'absent')
    .forEach((t) => rows.push(row({ key: `time:${t.key}`, label: t.label, value: t.display, isSet: t.state === 'set' })));
  (display.answers || []).forEach((a) => rows.push(row({ key: `answer:${a.key}`, label: a.label, value: a.display, isSet: a.state === 'set' })));

  // Rubrique dupliquée par personne : seule la personne de cette rubrique est montrée dans son groupe.
  const splitRef = pocket?.split?.refId || null;
  referenceGroups(display).forEach((group) => {
    const split = splitRef !== null && group.refs.some((ref) => ref.refId === splitRef);
    group.refs
      .filter((ref) => !split || ref.refId === splitRef)
      .forEach((ref) => rows.push(row({
        key: group.key,
        label: ROW_LABEL[group.key] || group.label,
        value: refText(ref),
        entity: entityName(ref.refId),
        refId: ref.refId
      })));
  });
  if (typeof display.notes === 'string' && display.notes.length > 0) {
    rows.push(row({ key: 'notes', label: display.notesLabel || 'Précisions', value: display.notes }));
  }

  // Champs complétés d'office (ex. « Préciser les heures » coché parce que des heures sont reprises)
  summary
    .filter((s) => !usedFields.has(s.field))
    .forEach((s) => rows.push({
      key: `field:${s.field}`,
      label: traced ? 'Complété d’office' : 'Repris de Pocket',
      value: '',
      entity: null,
      targets: [{ field: s.field, label: s.label, value: s.display }],
      status: TRACE_STATUS.mapped,
      note: ''
    }));

  return { rows, traced };
};
